import React, { useState, useEffect } from 'react';
import {
  Modal,
  Form,
  Input,
  InputNumber,
  Select,
  Row,
  Col,
  Checkbox,
  Button,
  Space,
  Card,
  Upload,
  message,
} from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import type { RcFile } from 'antd/es/upload';
import { surveyService } from '../../services/survey.service';
import { parseApiError } from '../../utils/error-handler';
import { Question, QuestionType, CreateQuestionData } from '../../types/survey.types';
import { normalizeQuestionOptions } from '../../utils/questionOptions';
import { parseElectoralOptionsCsv } from '../../utils/csvOptionsParser';
import { QuestionGroupDetail } from '../../types/survey-question-group.types';

interface QuestionBuilderModalProps {
  surveyId: string;
  question: Question | null;
  nextOrder: number;
  open: boolean;
  onSave: () => void;
  onCancel: () => void;
  availableGroups?: QuestionGroupDetail[];
  /** Spec 033: pesquisa marcada como eleitoral habilita o botão de importação de CSV. */
  surveyIsElectoral?: boolean;
}

export const QuestionBuilderModal: React.FC<QuestionBuilderModalProps> = ({
  surveyId,
  question,
  nextOrder,
  open,
  onSave,
  onCancel,
  availableGroups = [],
  surveyIsElectoral = false,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [questionType, setQuestionType] = useState<QuestionType>(
    question?.questionType || QuestionType.MULTIPLE_CHOICE
  );
  const [choices, setChoices] = useState<Array<{ value: string; label: string; filter?: string }>>([]);
  const maxSelections = Form.useWatch('maxSelections', form);
  const allowsTypedFilterWatched = Form.useWatch('allowsTypedFilter', form);

  // Spec 033 (adendo): "Exigir filtro" só faz sentido se a pergunta realmente tiver
  // como o respondente filtrar — senão as opções ficariam escondidas para sempre.
  const hasFilterCapability =
    Boolean(allowsTypedFilterWatched) || choices.some((c) => Boolean(c.filter && c.filter.trim()));

  useEffect(() => {
    if (!hasFilterCapability && form.getFieldValue('requiresFilterToShowOptions')) {
      form.setFieldsValue({ requiresFilterToShowOptions: false });
    }
  }, [hasFilterCapability, form]);

  useEffect(() => {
    if (open) {
      const opts = normalizeQuestionOptions(question?.options);
      const type = question?.questionType || QuestionType.MULTIPLE_CHOICE;
      setQuestionType(type);
      setChoices(opts.choices || []);
      form.setFieldsValue({
        order: question?.order ?? nextOrder,
        shortDescription: question?.shortDescription || '',
        longDescription: question?.longDescription || '',
        questionType: type,
        required: question?.required || false,
        allowsTypedFilter: question?.allowsTypedFilter || false,
        requiresFilterToShowOptions: question?.requiresFilterToShowOptions || false,
        filterLabel: question?.filterLabel || '',
        minRating: opts?.min ?? 1,
        maxRating: opts?.max ?? 5,
        randomizeChoices: Boolean(opts?.randomizeChoices),
        includeNullOption: Boolean(opts?.includeNullOption),
        includeDontKnowOption: Boolean(opts?.includeDontKnowOption),
        maxSelections: opts?.maxSelections ?? 0,
        enforceSelectionLimit: Boolean(opts?.enforceSelectionLimit),
        questionGroupId: (question as any)?.questionGroupId?.toString() ?? null,
      });
    }
  }, [open, question, nextOrder, form]);

  const handleSubmit = async (values: any) => {
    const isChoiceType =
      values.questionType === QuestionType.MULTIPLE_CHOICE ||
      values.questionType === QuestionType.CHECKBOX;

    const data: CreateQuestionData = {
      order: values.order,
      shortDescription: values.shortDescription,
      longDescription: values.longDescription || '',
      questionType: values.questionType as QuestionType,
      required: values.required || false,
      allowsTypedFilter: values.allowsTypedFilter || false,
      requiresFilterToShowOptions: values.requiresFilterToShowOptions || false,
      filterLabel: values.filterLabel?.trim() || null,
      questionGroupId: values.questionGroupId ?? null,
      options: isChoiceType
        ? {
            choices,
            randomizeChoices: Boolean(values.randomizeChoices),
            includeNullOption: Boolean(values.includeNullOption),
            includeDontKnowOption: Boolean(values.includeDontKnowOption),
            ...(values.questionType === QuestionType.CHECKBOX && {
              maxSelections: values.maxSelections ?? 0,
              enforceSelectionLimit: Boolean(values.enforceSelectionLimit),
            }),
          }
        : values.questionType === QuestionType.RATING
        ? { min: values.minRating, max: values.maxRating }
        : {},
    };

    try {
      setLoading(true);
      if (question) {
        await surveyService.updateQuestion(surveyId, question.id, data);
        message.success('Questão atualizada com sucesso!');
      } else {
        await surveyService.addQuestion(surveyId, data);
        message.success('Questão criada com sucesso!');
      }
      onSave();
    } catch (err) {
      message.error(parseApiError(err).message);
    } finally {
      setLoading(false);
    }
  };

  const addChoice = () => setChoices([...choices, { value: '', label: '', filter: '' }]);

  const updateChoice = (index: number, field: 'value' | 'label' | 'filter', value: string) => {
    const updated = [...choices];
    updated[index] = { ...updated[index], [field]: value };
    setChoices(updated);
  };

  const removeChoice = (index: number) =>
    setChoices(choices.filter((_, i) => i !== index));

  const isChoiceType =
    questionType === QuestionType.MULTIPLE_CHOICE ||
    questionType === QuestionType.CHECKBOX;

  const showElectoralImportButton = isChoiceType && surveyIsElectoral;

  const handleImportCsv = async (file: RcFile): Promise<boolean> => {
    const result = await parseElectoralOptionsCsv(file);
    if (!result.ok) {
      message.error(result.error);
      return false;
    }

    setChoices(result.choices);
    message.success(`${result.choices.length} opção(ões) importada(s) do CSV.`);
    return false; // impede o Upload de tentar enviar o arquivo ao servidor
  };

  return (
    <Modal
      title={question ? 'Editar Questão' : 'Nova Questão'}
      open={open}
      onCancel={onCancel}
      footer={null}
      width={700}
      centered
      destroyOnClose
    >
      <Form form={form} layout="vertical" onFinish={handleSubmit} autoComplete="off">
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              label="Ordem"
              name="order"
              rules={[{ required: true, message: 'Ordem é obrigatória' }]}
            >
              <InputNumber min={1} style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label="Tipo de Questão"
              name="questionType"
              rules={[{ required: true, message: 'Tipo é obrigatório' }]}
            >
              <Select onChange={(v) => setQuestionType(v as QuestionType)}>
                <Select.Option value={QuestionType.MULTIPLE_CHOICE}>
                  Múltipla Escolha
                </Select.Option>
                <Select.Option value={QuestionType.CHECKBOX}>
                  Caixas de Seleção
                </Select.Option>
                <Select.Option value={QuestionType.TEXT}>Texto Livre</Select.Option>
                <Select.Option value={QuestionType.NUMBER}>Número</Select.Option>
                <Select.Option value={QuestionType.DATE}>Data</Select.Option>
                <Select.Option value={QuestionType.RATING}>Avaliação</Select.Option>
              </Select>
            </Form.Item>
          </Col>
        </Row>

        <Form.Item
          label="Título da Questão"
          name="shortDescription"
          rules={[
            { required: true, message: 'Título é obrigatório' },
            { min: 3, message: 'Mínimo 3 caracteres' },
          ]}
        >
          <Input placeholder="Digite o título da questão" />
        </Form.Item>

        <Form.Item label="Descrição Detalhada" name="longDescription">
          <Input.TextArea placeholder="Descrição mais detalhada (opcional)" rows={3} />
        </Form.Item>

        {isChoiceType && (
          <>
            {showElectoralImportButton && (
              <Upload accept=".csv" showUploadList={false} beforeUpload={handleImportCsv}>
                <Button icon={<UploadOutlined />} style={{ marginBottom: 8 }}>
                  Importar opções eleitorais
                </Button>
              </Upload>
            )}
            <Card title="Opções de Resposta" size="small" style={{ marginBottom: 16 }}>
              <Space direction="vertical" style={{ width: '100%' }}>
                {choices.map((choice, index) => (
                  <Space key={index} style={{ width: '100%' }} size={8}>
                    <Input
                      placeholder="Valor"
                      value={choice.value}
                      onChange={(e) => updateChoice(index, 'value', e.target.value)}
                      style={{ flex: 1 }}
                    />
                    <Input
                      placeholder="Rótulo/Descrição"
                      value={choice.label}
                      onChange={(e) => updateChoice(index, 'label', e.target.value)}
                      style={{ flex: 1 }}
                    />
                    <Input
                      placeholder="Filtro (opcional)"
                      value={choice.filter || ''}
                      onChange={(e) => updateChoice(index, 'filter', e.target.value)}
                      style={{ flex: 1 }}
                    />
                    <Button
                      type="primary"
                      danger
                      size="small"
                      onClick={() => removeChoice(index)}
                    >
                      Remover
                    </Button>
                  </Space>
                ))}
                <Button type="dashed" block onClick={addChoice}>
                  + Adicionar Opção
                </Button>
              </Space>
            </Card>

            <Form.Item name="randomizeChoices" valuePropName="checked" style={{ marginBottom: 8 }}>
              <Checkbox>Ordenar aleatoriamente</Checkbox>
            </Form.Item>
            <Form.Item name="includeNullOption" valuePropName="checked" style={{ marginBottom: 8 }}>
              <Checkbox>Incluir Nulo</Checkbox>
            </Form.Item>
            <Form.Item
              name="includeDontKnowOption"
              valuePropName="checked"
              style={{ marginBottom: 16 }}
            >
              <Checkbox>Incluir Não sei responder</Checkbox>
            </Form.Item>

            {questionType === QuestionType.CHECKBOX && (
              <Row gutter={16} style={{ marginBottom: 16 }}>
                <Col span={12}>
                  <Form.Item label="Limite máximo de opções" name="maxSelections">
                    <InputNumber min={0} max={20} style={{ width: '100%' }} placeholder="0 = sem limite" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="enforceSelectionLimit" valuePropName="checked" style={{ marginTop: 30 }}>
                    <Checkbox disabled={!maxSelections || maxSelections === 0}>
                      Obrigar a quantidade de limite
                    </Checkbox>
                  </Form.Item>
                </Col>
              </Row>
            )}
          </>
        )}

        {questionType === QuestionType.RATING && (
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item label="Valor Mínimo" name="minRating">
                <InputNumber min={1} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="Valor Máximo" name="maxRating">
                <InputNumber min={1} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>
        )}

        <Form.Item name="required" valuePropName="checked" style={{ marginBottom: 8 }}>
          <Checkbox>Questão obrigatória</Checkbox>
        </Form.Item>

        {isChoiceType && (
          <>
            <Form.Item name="allowsTypedFilter" valuePropName="checked" style={{ marginBottom: 8 }}>
              <Checkbox>Permite filtro digitado</Checkbox>
            </Form.Item>
            <Form.Item
              name="filterLabel"
              label="Rótulo do filtro (opcional)"
              rules={[{ max: 100, message: 'Máximo de 100 caracteres' }]}
              style={{ marginBottom: 8 }}
            >
              <Input
                maxLength={100}
                showCount
                placeholder='Ex.: "Filtre os candidatos pelo partido:"'
              />
            </Form.Item>
            <Form.Item name="requiresFilterToShowOptions" valuePropName="checked" style={{ marginBottom: 4 }}>
              <Checkbox disabled={!hasFilterCapability}>Exigir filtro antes de exibir opções</Checkbox>
            </Form.Item>
            {!hasFilterCapability && (
              <div style={{ color: '#999', fontSize: 12, marginTop: -4, marginBottom: 8 }}>
                Disponível quando houver ao menos um valor de filtro nas opções ou "Permite filtro digitado" estiver habilitado.
              </div>
            )}
          </>
        )}

        {availableGroups.length > 0 && (
          <Form.Item label="Grupo de perguntas" name="questionGroupId">
            <Select
              allowClear
              placeholder="Sem grupo (pergunta avulsa)"
              options={availableGroups.map((g) => ({
                value: g.id,
                label: `${g.name}${g.scope === 'STATE' && g.stateName ? ` — ${g.stateName}` : ''}${g.scope === 'NATIONAL' ? ' (Nacional)' : ' (Estadual)'}`,
              }))}
            />
          </Form.Item>
        )}

        <Space style={{ width: '100%', justifyContent: 'flex-end', marginTop: 24 }}>
          <Button onClick={onCancel}>Cancelar</Button>
          <Button type="primary" htmlType="submit" loading={loading}>
            {question ? 'Atualizar Questão' : 'Criar Questão'}
          </Button>
        </Space>
      </Form>
    </Modal>
  );
};
